// SPDX-License-Identifier: AGPL-3.0-or-later

import {UNLIMITED_USER_LIMIT} from '@app/features/app/config/I18nDisplayConstants';
import styles from '@app/features/channel/components/modals/channel_tabs/ChannelOverviewTab.module.css';
import {
	BITRATE_OPTIONS,
	type FormInputs,
} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/shared';
import {Slider} from '@app/features/ui/components/Slider';
import {
	VOICE_CHANNEL_CONNECTION_LIMIT_DEFAULT,
	VOICE_CHANNEL_CONNECTION_LIMIT_MAX,
	VOICE_CHANNEL_CONNECTION_LIMIT_MIN,
} from '@fluxer/constants/src/LimitConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import {Controller, type UseFormReturn} from 'react-hook-form';

const USER_LIMIT_HELPER_DESCRIPTOR = msg({
	message: 'Maximum members who can join at once. {limit} means unlimited.',
	comment: 'Voice channel user-limit helper text. limit is the UI value that represents no limit.',
});
const CONNECTION_LIMIT_LABEL_DESCRIPTOR = msg({
	message: 'Connection limit',
	comment: 'Voice channel setting label for the maximum number of active connections per member.',
});
const CONNECTION_LIMIT_HELPER_DESCRIPTOR = msg({
	message: 'Maximum active connections one member can keep in this channel.',
	comment: 'Voice channel per-member connection-limit helper text.',
});

interface VoiceSettingsProps {
	form: UseFormReturn<FormInputs>;
}

export const VoiceSettings: React.FC<VoiceSettingsProps> = ({form}) => {
	const {i18n} = useLingui();
	return (
		<>
			<div data-flx="channel.channel-tabs.channel-overview-tab.div--3">
				<div className={styles.fieldLabel} data-flx="channel.channel-tabs.channel-overview-tab.field-label">
					<Trans>Voice quality</Trans>
				</div>
				<Controller
					name="bitrate"
					control={form.control}
					render={({field}) => {
						const sliderValue = typeof field.value === 'number' ? field.value : BITRATE_OPTIONS[1];
						return (
							<div className={styles.fieldContent} data-flx="channel.channel-tabs.channel-overview-tab.field-content">
								<Slider
									defaultValue={sliderValue}
									minValue={BITRATE_OPTIONS[0]}
									maxValue={BITRATE_OPTIONS[BITRATE_OPTIONS.length - 1]}
									markers={Array.from(BITRATE_OPTIONS)}
									stickToMarkers={true}
									onMarkerRender={(value) => <Trans>{value} kbps</Trans>}
									onValueRender={(value) => <Trans>{value} kbps</Trans>}
									factoryDefaultValue={64}
									onValueChange={(value) => field.onChange(value)}
									data-flx="channel.channel-tabs.channel-overview-tab.slider"
								/>
								<p className={styles.fieldNote} data-flx="channel.channel-tabs.channel-overview-tab.field-note">
									<Trans>Higher bitrate = better quality and higher bandwidth usage.</Trans>
								</p>
							</div>
						);
					}}
					data-flx="channel.channel-tabs.channel-overview-tab.controller--2"
				/>
			</div>
			<div data-flx="channel.channel-tabs.channel-overview-tab.div--4">
				<div className={styles.fieldLabel} data-flx="channel.channel-tabs.channel-overview-tab.field-label--2">
					<Trans>Participant limit</Trans>
				</div>
				<Controller
					name="user_limit"
					control={form.control}
					render={({field}) => {
						const currentValue = typeof field.value === 'number' ? field.value : 0;
						return (
							<div
								className={styles.fieldContent}
								data-flx="channel.channel-tabs.channel-overview-tab.field-content--2"
							>
								<Slider
									defaultValue={currentValue}
									minValue={0}
									maxValue={99}
									step={1}
									markers={[0, 25, 50, 75, 99]}
									onMarkerRender={(value) => (value === 0 ? '∞' : value)}
									onValueRender={(value) =>
										value === 0 ? <Trans>No limit</Trans> : <Trans>{value} participants</Trans>
									}
									factoryDefaultValue={0}
									onValueChange={(value) => field.onChange(value)}
									data-flx="channel.channel-tabs.channel-overview-tab.slider--2"
								/>
								<p className={styles.fieldNote} data-flx="channel.channel-tabs.channel-overview-tab.field-note--2">
									{i18n._(USER_LIMIT_HELPER_DESCRIPTOR, {limit: UNLIMITED_USER_LIMIT})}
								</p>
							</div>
						);
					}}
					data-flx="channel.channel-tabs.channel-overview-tab.controller--3"
				/>
			</div>
			<div data-flx="channel.channel-tabs.channel-overview-tab.div--5">
				<div className={styles.fieldLabel} data-flx="channel.channel-tabs.channel-overview-tab.field-label--3">
					{i18n._(CONNECTION_LIMIT_LABEL_DESCRIPTOR)}
				</div>
				<Controller
					name="voice_connection_limit"
					control={form.control}
					render={({field}) => {
						const currentValue = typeof field.value === 'number' ? field.value : VOICE_CHANNEL_CONNECTION_LIMIT_DEFAULT;
						return (
							<div
								className={styles.fieldContent}
								data-flx="channel.channel-tabs.channel-overview-tab.field-content--3"
							>
								<Slider
									defaultValue={currentValue}
									minValue={VOICE_CHANNEL_CONNECTION_LIMIT_MIN}
									maxValue={VOICE_CHANNEL_CONNECTION_LIMIT_MAX}
									step={1}
									markers={[1, 5, 25, 50, 100]}
									onMarkerRender={(value) => value}
									onValueRender={(value) => <Trans>{value} connections</Trans>}
									factoryDefaultValue={VOICE_CHANNEL_CONNECTION_LIMIT_DEFAULT}
									onValueChange={(value) => field.onChange(value)}
									data-flx="channel.channel-tabs.channel-overview-tab.slider--3"
								/>
								<p className={styles.fieldNote} data-flx="channel.channel-tabs.channel-overview-tab.field-note--3">
									{i18n._(CONNECTION_LIMIT_HELPER_DESCRIPTOR)}
								</p>
							</div>
						);
					}}
					data-flx="channel.channel-tabs.channel-overview-tab.controller--4"
				/>
			</div>
		</>
	);
};
